import React, { useState } from 'react';
import { Search, Package, Truck, CheckCircle, Clock, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STEPS = [
  { key: 'pending', label: 'Order Placed', icon: Clock },
  { key: 'confirmed', label: 'Confirmed', icon: CheckCircle },
  { key: 'processing', label: 'Processing', icon: Package },
  { key: 'shipped', label: 'Shipped', icon: Truck },
  { key: 'delivered', label: 'Delivered', icon: CheckCircle }
];

const getStatusIndex = (status) => {
  const idx = STATUS_STEPS.findIndex(s => s.key === status);
  return idx >= 0 ? idx : 0;
};

export function TrackOrder() {
  const [orderNumber, setOrderNumber] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!orderNumber.trim()) {
      toast.error('Please enter an order number');
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const res = await fetch(`${API}/api/orders/track/${orderNumber.trim()}`);
      if (res.ok) {
        const data = await res.json();
        setOrder(data);
      } else {
        setOrder(null);
        toast.error('Order not found. Please check your order number.');
      }
    } catch {
      toast.error('Failed to track order');
      setOrder(null);
    }
    setLoading(false);
  };

  const currentStep = order ? getStatusIndex(order.order_status) : 0;

  return (
    <div className="min-h-[70vh] bg-gradient-to-b from-orange-50 to-white py-12">
      <div className="max-w-3xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
            <Package className="w-8 h-8 text-orange-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Track Your Order</h1>
          <p className="text-gray-600 mt-2">Enter your order number to check delivery status</p>
        </div>

        <Card className="mb-8 shadow-lg border-orange-100" data-testid="track-order-form">
          <CardContent className="pt-6">
            <form onSubmit={handleTrack} className="flex gap-3">
              <Input
                placeholder="Enter Order Number (e.g., ORD-ABC123)"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                className="flex-1 border-orange-200 focus:border-orange-400"
                data-testid="order-number-input"
              />
              <Button 
                type="submit" 
                disabled={loading}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="track-order-btn"
              >
                {loading ? 'Searching...' : <><Search className="w-4 h-4 mr-2" /> Track</>}
              </Button>
            </form>
          </CardContent>
        </Card>

        {searched && !order && !loading && (
          <Card className="border-red-100 bg-red-50" data-testid="order-not-found">
            <CardContent className="py-8 text-center">
              <p className="text-red-600 font-medium">Order not found</p>
              <p className="text-gray-500 text-sm mt-1">Please verify your order number and try again</p>
            </CardContent>
          </Card>
        )}

        {order && (
          <div className="space-y-6" data-testid="order-details">
            <Card className="shadow-lg border-orange-100">
              <CardHeader className="bg-orange-50 border-b border-orange-100">
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <CardTitle className="text-lg text-gray-900">
                    Order #{order.order_number}
                  </CardTitle>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    order.order_status === 'delivered' ? 'bg-green-100 text-green-700' :
                    order.order_status === 'shipped' ? 'bg-blue-100 text-blue-700' :
                    order.order_status === 'cancelled' ? 'bg-red-100 text-red-700' :
                    'bg-orange-100 text-orange-700'
                  }`}>
                    {order.order_status?.charAt(0).toUpperCase() + order.order_status?.slice(1)}
                  </span>
                </div>
                {order.user_name && (
                  <p className="text-sm text-gray-500">Hello, {order.user_name}!</p>
                )}
              </CardHeader>
              <CardContent className="pt-6">
                <div className="mb-8">
                  <div className="flex justify-between items-center relative">
                    <div className="absolute top-5 left-0 right-0 h-1 bg-gray-200 -z-10" />
                    <div 
                      className="absolute top-5 left-0 h-1 bg-orange-500 -z-10 transition-all duration-500"
                      style={{ width: `${(currentStep / (STATUS_STEPS.length - 1)) * 100}%` }}
                    />
                    {STATUS_STEPS.map((step, idx) => {
                      const Icon = step.icon;
                      const isActive = idx <= currentStep;
                      return (
                        <div key={step.key} className="flex flex-col items-center z-10">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isActive ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-400'
                          }`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <span className={`text-xs mt-2 text-center ${isActive ? 'text-orange-600 font-medium' : 'text-gray-400'}`}>
                            {step.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Package className="w-4 h-4 text-orange-600" /> Items Ordered
                    </h3>
                    <div className="space-y-2">
                      {order.items?.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm bg-gray-50 p-2 rounded">
                          <span className="text-gray-700">{item.name} × {item.quantity}</span>
                          <span className="font-medium">₹{(item.price * item.quantity).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                    <div className="border-t mt-3 pt-3 space-y-1 text-sm">
                      <div className="flex justify-between text-gray-500">
                        <span>Subtotal</span><span>₹{order.subtotal?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-gray-500">
                        <span>Shipping</span><span>{order.shipping === 0 ? 'FREE' : `₹${order.shipping}`}</span>
                      </div>
                      <div className="flex justify-between text-gray-500">
                        <span>Tax (18% GST)</span><span>₹{order.tax?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between font-bold text-gray-900 pt-2 border-t">
                        <span>Total</span><span>₹{order.total?.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-orange-600" /> Delivery Info
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg text-sm space-y-2">
                      <p className="text-gray-700">
                        <span className="text-gray-500">City:</span> {order.shipping_city}
                      </p>
                      <p className="text-gray-700">
                        <span className="text-gray-500">State:</span> {order.shipping_state}
                      </p>
                      <p className="text-gray-700">
                        <span className="text-gray-500">Pincode:</span> {order.shipping_pincode}
                      </p>
                      <p className="text-gray-700">
                        <span className="text-gray-500">Payment:</span> {order.payment_method === 'upi' ? 'UPI' : order.payment_method === 'bank' ? 'Bank Transfer' : order.payment_method === 'cod' ? 'Cash on Delivery' : 'Online Payment'}
                      </p>
                      <p className="text-gray-700">
                        <span className="text-gray-500">Payment Status:</span>{' '}
                        <span className={order.payment_status === 'paid' ? 'text-green-600' : 'text-orange-600'}>
                          {order.payment_status?.charAt(0).toUpperCase() + order.payment_status?.slice(1)}
                        </span>
                      </p>
                      {order.created_at && (
                        <p className="text-gray-700">
                          <span className="text-gray-500">Order Date:</span>{' '}
                          {new Date(order.created_at).toLocaleDateString('en-IN', {
                            day: 'numeric', month: 'short', year: 'numeric'
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="text-center text-sm text-gray-500">
              <p>Need help? Contact us at <a href="tel:+919831072381" className="text-orange-600 hover:underline">+91 9831072381</a></p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
